import React, { useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { DeploymentPlayer } from 'cccisd-laravel-assignment';
import { useParams } from 'cccisd-react-router';
import { client } from 'cccisd-apollo';
import Loader from 'cccisd-loader';
import { CV_URL } from '../../../pages/Welcome';
import IntroModal from './IntroModal.js';
import _find from 'lodash/find';
import _get from 'lodash/get';
import planQuery from './getPlan.graphql';
import IconLock from 'cccisd-icons/lock2';

const Boilerplate = window.cccisd.boilerplate;
const Fortress = window.cccisd.fortress;

const SessionPlayer = props => {
    const { assignmentPlanId, deploymentId, sessionId, pawnId, pawnHash } = useParams();
    const urlParams = new URLSearchParams(window.location.search);
    const surveyParamsString = urlParams.get('s');
    const surveyParams = surveyParamsString ? surveyParamsString.split(',') : [];
    const redirectTimeout = useRef(null);

    const [isLoading, setIsLoading] = useState(true);
    const [isOpen, setIsOpen] = useState(true);
    const [allowLanguageSelection, setAllowLanguageSelection] = useState(true);
    const [sessionInfo, setSessionInfo] = useState({});

    useEffect(() => {
        getSessionData();

        return () => {
            if (redirectTimeout?.current) {
                clearTimeout(redirectTimeout.current);
            }
        };
    }, []);

    const getSessionData = async () => {
        let sessionInfoRes = {
            isStarted: false,
            providerName: '',
            schoolName: '',
            studentName: '',
            sessionCollection: [],
            surveyHandles: surveyParams,
        };
        try {
            const resp = await client.query({
                query: planQuery,
                variables: {
                    sessionId: Number(sessionId),
                    assignmentPlanId: Number(assignmentPlanId),
                    pawnId: Number(pawnId),
                    pawnHash,
                    surveyHandles: surveyParams,
                    deploymentId,
                },
                fetchPolicy: 'network-only',
            });
            const plan = resp.data.flows.assignmentPlan;

            const surveyList = resp.data.flows.surveyList;
            if (surveyList) {
                let countEnglish = 0;
                let countSpanish = 0;
                surveyList.forEach(survey => {
                    survey.flowList.forEach(flow => {
                        flow.language === 'en' ? countEnglish++ : countSpanish++;
                    });
                });
                if (countEnglish !== countSpanish) {
                    setAllowLanguageSelection(false);
                }
            }

            const respondentRole = _get(resp, 'data.roles.anyRole.role.handle', null);
            let studentInfo = {};
            if (respondentRole === 'learner') {
                studentInfo = _get(resp, 'data.roles.anyRole.fields', {});
                sessionInfoRes.providerName = _get(resp, 'data.roles.anyRole.parentRoles.instructor.user.fullName', '');
                sessionInfoRes.schoolName = _get(resp, 'data.roles.anyRole.parentGroup.site.group.label', '');
            } else if (respondentRole === 'contact') {
                studentInfo = _get(resp, 'data.roles.anyRole.parentRoles.learner.fields', {});
                sessionInfoRes.providerName = _get(
                    resp,
                    'data.roles.anyRole.parentRoles.learner.parentRoles.instructor.user.fullName',
                    ''
                );
                sessionInfoRes.schoolName = _get(
                    resp,
                    'data.roles.anyRole.parentRoles.learner.parentGroup.site.group.label',
                    ''
                );
            }

            let studentDisplayName = '';
            if (studentInfo?.firstName || studentInfo?.lastName) {
                studentDisplayName = `${studentInfo.firstName || ''} ${studentInfo.lastName || ''}`;
            } else if (studentInfo?.email) {
                studentDisplayName = studentInfo.email;
            }

            props.setDisplayName(studentDisplayName);

            setSessionInfo({
                ...sessionInfoRes,
                isStarted: !!plan.session.assignmentProgress?.startedAt,
                studentName: studentDisplayName,
                sessionCollection: plan.session.settings.sessionCollection,
                reporterType: respondentRole,
            });

            // eslint-disable-next-line eqeqeq
            const contactSessionDeploymentInfo = _find(plan.session.sessionDeploymentInfo, o => o.pawnId == pawnId);
            if (contactSessionDeploymentInfo?.isCancelled) {
                setIsOpen(false);
            }
        } catch (e) {
            throw e;
        }
        setIsLoading(false);
    };

    function handleRedirectOnComplete() {
        let redirectTo = null;
        switch (Fortress.user.acting.role.handle) {
            case 'guest':
                redirectTo = CV_URL;
                break;
            case 'instructor':
                redirectTo = Boilerplate.url('/administer');
                break;
            default:
                redirectTo = Boilerplate.url('/authRedirect');
                break;
        }

        if (redirectTo) {
            // give them time to read the deployment completion message
            redirectTimeout.current = setTimeout(() => {
                // if "back" button is clicked, don't return to unique deployment link
                window.location.replace(redirectTo);
            }, 5000);
        }
    }

    return (
        <Loader loading={isLoading} removeChildren>
            {!isLoading && isOpen && (
                <div className="cccisd-share-session-player">
                    {/* Use style tag because there's no way to pass JS variable in to CSS file */}
                    <style key={props.headerHeight}>{`
                        .cccisd-share-session-player > div {
                          min-height: calc(100vh - ${props.headerHeight}px);
                        }
                    `}</style>

                    <IntroModal sessionInfo={sessionInfo} />

                    <DeploymentPlayer
                        deploymentId={Number(deploymentId)}
                        assignmentOptions={surveyParams}
                        sortByAssignmentOptions
                        pawnId={Number(pawnId)}
                        pawnHash={pawnHash}
                        sessionId={Number(sessionId)}
                        onComplete={handleRedirectOnComplete}
                        language={allowLanguageSelection ? null : 'en'}
                        allowLanguageChange={allowLanguageSelection}
                    />
                </div>
            )}
            {!isLoading && !isOpen && (
                <div className="alert alert-warning" role="alert">
                    <IconLock spaceRight />
                    This timepoint is closed or has been cancelled
                </div>
            )}
        </Loader>
    );
};

SessionPlayer.propTypes = {
    headerHeight: PropTypes.number,
    setDisplayName: PropTypes.func,
};

SessionPlayer.defaultProps = {
    headerHeight: 0,
    setDisplayName: () => {},
};

export default SessionPlayer;
